import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, TextInputHelperCurrency } from '@honeycomb-npm/honeycomb-react';
import { DynamicContent } from '@commblades/commblades';

import { AmountValidation, dropdownOptions } from './yourFinanceTypes';
import { HiddenLabelWrapper } from './AmountDataField.styled';

interface AmountDataFieldProps {
    fieldId: string;
    recordKey: string;
    label: string;
    inputPlaceHolder?: string;
    helptext?: string;
    frequencyOptions?: dropdownOptions[];
    validation: AmountValidation;
    allowIgnoreAmount?: boolean;
    amountInputRef?: React.RefObject<HTMLInputElement>;
    hideFrequency?: boolean;
    defaultDropdownValue?: string;
    handleAmountChange: (name: string, value: string, recordKey: string) => void;
    handleDropdownChange: (name: string, value: string, recordKey: string) => void;
    children?: React.ReactNode;
}

const AmountDataField: React.FC<AmountDataFieldProps> = ({
    fieldId,
    recordKey,
    label,
    inputPlaceHolder,
    helptext,
    frequencyOptions,
    validation,
    allowIgnoreAmount,
    hideFrequency,
    amountInputRef,
    defaultDropdownValue,
    handleAmountChange,
    handleDropdownChange,
    children,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [frequencyValue, setFrequencyValue] = useState('');

    const frequencyDropdownRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
        setInputValue('');

        if (frequencyOptions && frequencyOptions?.length > 0) {
            const defaultOption = frequencyOptions.find((o) => o.value === defaultDropdownValue);

            const initOptionValue = defaultOption?.value || frequencyOptions[0]?.value || '';

            setFrequencyValue(initOptionValue);
            handleDropdownChange('', initOptionValue, recordKey);
        }
    }, []);

    const handleAmountInputChange = (name: string, value: string) => {
        setInputValue(value);
        handleAmountChange(name, value, recordKey);
    };

    const handleDropdownValueChange = (name: string, value: string) => {
        setFrequencyValue(value);
        handleDropdownChange(name, value, recordKey);
    };

    return (
        <>
            <h3 id={`${fieldId}-heading`} className="mt-1 mb-1">
                <DynamicContent content={label} id={`${fieldId}-heading-content`} />
            </h3>
            <div className="row ml-0">
                <HiddenLabelWrapper className="col-md-4 p-0 pr-5 col-12">
                    <TextInputHelperCurrency
                        id={`${fieldId}-amount-input`}
                        aria-labelledby={`${fieldId}-heading`}
                        name={`${fieldId}-amount-input`}
                        label=""
                        value={inputValue}
                        placeholder={inputPlaceHolder || ''}
                        helptext={helptext || ''}
                        min={Number(validation?.minValue || 0)}
                        max={Number(validation?.maxValue || 9999999)}
                        errormessage={validation?.defaultErrorMessage}
                        errormessageMin={Number(inputValue) < 0
                            ? validation?.negativeValueMessage
                            : validation?.minValueMessage}
                        errormessageMax={validation?.maxValueMessage}
                        required={!allowIgnoreAmount}
                        precision={2}
                        onChange={handleAmountInputChange}
                        forwardedRef={amountInputRef}
                        inputMode="decimal"
                        autoComplete="off"
                    />
                </HiddenLabelWrapper>
                {!hideFrequency && (
                    <HiddenLabelWrapper className="col-md-4 p-0 pr-5 col-12">
                        <Dropdown
                            id={`${fieldId}-frequency-dropdown`}
                            name={`${fieldId}-frequency-dropdown`}
                            label={label}
                            isValid
                            options={frequencyOptions?.map((o) => ({ label: o.value, value: o.value }))}
                            value={frequencyValue}
                            onChange={handleDropdownValueChange}
                            hideDefaultOption
                            ref={frequencyDropdownRef}
                            aria-label="frequency dropdown"
                        />
                    </HiddenLabelWrapper>
                )}
                {children && (
                    <HiddenLabelWrapper className="col-md-4 p-0 pr-5 col-12">
                        {children}
                    </HiddenLabelWrapper>
                )}
            </div>
        </>
    );
};

export default AmountDataField;
